import 'react-responsive-carousel/lib/styles/carousel.min.css';

import type {ClickOnboardingCarouselProperties, ClickPaywallCarouselProperties} from '@gaia/ampli';
import {CONIFER_3, GRANITE_3, GRANITE_7} from '@gaia/colors';
import Button from '@gaia/components/Button';
import GaiaIcon from '@gaia/icons/GaiaIcon';
import theme from '@gaia/theme';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {IconButton} from '@mui/material';
import {createContext, useState} from 'react';
import {Carousel} from 'react-responsive-carousel';

import colorWaypoints from './colorWaypoints.png';
import layerMapsTogether from './layerMapsTogether.png';
import mapLayers from './mapLayers.png';
import navigateOffline from './navigateOffline.png';
import OutsidePlusCard from './OutsidePlusCard';
import PricingCard from './PricingCard';
import printMaps from './printMaps.png';
import UpsellFeatureCard, {UpsellFeatureCardProps} from './UpsellFeatureCard';

export interface UpsellCarouselProps extends React.HTMLProps<HTMLDivElement> {
    selectedScreen?: UpsellFeatureCardProps['key'];
    onClose: () => void;
}

// this is used to make room above the card for the carousel navigation buttons
const cardContainerStyle = {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(5),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
};

// will be replaced or updated with final feature data
const featureCards: UpsellFeatureCardProps[] = [
    {
        key: 'viewAllMaps',
        headline: 'View All the Maps',
        bodyText: 'Topographic, Satellite Imagery, National Geographic, MVUM, and tons more.',
        imgSrc: mapLayers,
        imgAltText: 'Gaia GPS premium includes many more map layers',
    },
    {
        key: 'customizeWaypoints',
        headline: 'Customize Waypoints',
        bodyText: 'Mark up the map with icons and emojis, and customize with colors.',
        imgSrc: colorWaypoints,
        imgAltText: 'Gaia GPS premium allows you to customize the color of your waypoints',
    },
    {
        key: 'print',
        headline: 'Print',
        bodyText: 'Print any map so you always have a paper backup.',
        imgSrc: printMaps,
        imgAltText: 'Gaia GPS premium includes printing of all available map layers',
    },
    {
        key: 'navigateOffline',
        headline: 'Navigate Offline',
        bodyText: 'Download your maps and take them offline, deep in the backcountry.',
        imgSrc: navigateOffline,
        imgAltText:
            'Gaia GPS premium allows downloading maps to your mobile device to access without cell reception',
    },
    {
        key: 'layerMapsTogether',
        headline: 'Layer Maps Together',
        bodyText: 'Discover even more terrain details by stacking your maps.',
        imgSrc: layerMapsTogether,
        imgAltText: 'Gaia GPS premium allows stacking map layers to view simultaneously',
    },
];

type UpsellCarouselAnalyticsProps = {
    analyticsContext: 'onboarding' | 'paywall';
    currentIndex: number;
    paywallTrigger: ClickPaywallCarouselProperties['paywallTrigger'] | undefined;
};

type UpsellCarouselClickAction =
    | ClickPaywallCarouselProperties['action']
    | ClickOnboardingCarouselProperties['action'];

function createAnalyticsFunction({
    analyticsContext,
    currentIndex,
    paywallTrigger,
}: UpsellCarouselAnalyticsProps) {
    // determine which cards are actually included in this instance of the upsell carousel
    const usedFeatureCards = paywallTrigger
        ? featureCards.filter((card) => card.key === paywallTrigger)
        : featureCards;
    const analyticsCardData: ClickPaywallCarouselProperties['cardViewed'][] = [
        ...usedFeatureCards.map((f) => f.key),
        'pricing',
        'outsidePlus',
    ];
    const cardViewed = analyticsCardData[currentIndex];
    return function fireAnalytics(action: UpsellCarouselClickAction) {
        if (analyticsContext === 'onboarding') {
            ampli.clickOnboardingCarousel({
                action: action as ClickOnboardingCarouselProperties['action'],
                cardViewed,
            });
        } else {
            ampli.clickPaywallCarousel({
                action,
                cardViewed,
                paywallTrigger: paywallTrigger!,
            });
        }
    };
}
export const UpsellCarouselAnalyticsFunctionContext = createContext<
    (action: UpsellCarouselClickAction) => void
>((_action) => {
    throw new Error('UpsellCarouselAnalyticsFunctionContext not initialized');
});
UpsellCarouselAnalyticsFunctionContext.displayName = 'Upsell Carousel Analytics Function Context';

/**
 * @param selectedScreen - if present, only the selected feature card is shown in the carousel. if `undefined`, all upsell cards will be shown
 */
export function UpsellCarousel({selectedScreen, onClose}: UpsellCarouselProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const analyticsContext = !selectedScreen ? 'onboarding' : 'paywall';
    const paywallTrigger = selectedScreen as ClickPaywallCarouselProperties['paywallTrigger']; // these types are effectively the same on web

    const fireAnalytics = createAnalyticsFunction({
        analyticsContext,
        paywallTrigger,
        currentIndex,
    });

    const cards = (
        selectedScreen ? featureCards.filter((card) => card.key === selectedScreen) : featureCards
    ).map(({key, ...props}) => <UpsellFeatureCard {...props} key={key} />);
    cards.push(<PricingCard />);
    cards.push(<OutsidePlusCard />);

    const nextCard = () => {
        if (currentIndex < cards.length - 1) {
            setCurrentIndex(currentIndex + 1);
            fireAnalytics('Select - Next Card');
        }
    };
    const previousCard = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            fireAnalytics('Select - Previous Card');
        }
    };

    const NavButtonPrev = () =>
        currentIndex > 0 ? (
            <Button
                appearance='secondary'
                onClick={previousCard}
                style={{
                    zIndex: 1,
                    left: theme.spacing(1),
                    position: 'absolute',
                    top: 0,
                }}
            >
                Previous
            </Button>
        ) : null;

    const NavButtonNext = () =>
        // display the "Next" button unless viewing the last card of the carousel
        // when viewing the last card, display a "close" button
        currentIndex < cards.length - 1 ? (
            <Button
                appearance='default'
                onClick={nextCard}
                style={{
                    right: theme.spacing(1),
                    position: 'absolute',
                    top: 0,
                }}
            >
                Next
            </Button>
        ) : (
            <IconButton
                onClick={() => {
                    fireAnalytics('Select - Close');
                    onClose();
                }}
                size='small'
                style={{
                    position: 'absolute',
                    right: theme.spacing(1),
                    top: theme.spacing(0.5),
                }}
                aria-label='Close modal'
            >
                <GaiaIcon name='cancel' style={{color: GRANITE_3}} />
            </IconButton>
        );

    function renderIndicator(onClickHandler: any, isSelected: any, cardIndex: number) {
        const indicatorStyles: React.CSSProperties = {
            transition: 'opacity .25s ease-in',
            background: isSelected ? CONIFER_3 : GRANITE_7,
            borderRadius: '50%',
            width: 8,
            height: 8,
            display: 'inline-block',
            margin: `0 ${theme.spacing(1)}`,
            position: 'relative',
        };

        return (
            <li style={indicatorStyles} value={cardIndex} key={cardIndex}>
                {!isSelected && (
                    <span
                        aria-label={`Showing card ${cardIndex + 1}`}
                        style={{
                            border: 0,
                            background: 'none',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            padding: 0,
                        }}
                    />
                )}
            </li>
        );
    }

    return (
        <div
            style={{
                maxWidth: 410,
                minWidth: 230,
            }}
        >
            <UpsellCarouselAnalyticsFunctionContext.Provider value={fireAnalytics}>
                <Carousel
                    selectedItem={currentIndex}
                    showStatus={false}
                    showThumbs={false}
                    renderArrowPrev={NavButtonPrev}
                    renderArrowNext={NavButtonNext}
                    renderIndicator={renderIndicator}
                    width={'100%'}
                >
                    {cards.map((card, i) => (
                        <div style={cardContainerStyle} key={i}>
                            {card}
                        </div>
                    ))}
                </Carousel>
            </UpsellCarouselAnalyticsFunctionContext.Provider>
        </div>
    );
}
